import { useId, type ReactNode } from "react";
import { Link, useResolvedPath } from "react-router-dom";
import { PageHeader, type PageHeaderAction } from "@a2z/ui";
import { usePublishPageAction } from "./PageActionSlot";
import { pageFrameClass } from "./pageFrame";

/**
 * The page primitive every back-office screen renders inside (decision D-F).
 *
 * It now owns exactly one thing — WIDTH — and delegates the header itself to
 * `PageHeader` in `@a2z/ui`. It used to render its own: a 16px/600 heading where
 * the seller portal's is 24px/700, which made the page name visibly smaller in
 * this app than in the rest of the platform on every single screen. Two headers
 * solving one problem is precisely what the shared library exists to stop.
 *
 * The width rules stay here because they ARE this app's:
 *
 * - **Width.** Lists cap at 720–960px and forms at 560–640px, because a form
 *   read at a full desktop width loses the eye between label and control.
 * - **Alignment.** The container centers inside the main area; the CONTENT
 *   inside it aligns to the container's start, so the page header and the list
 *   below it share one start edge. The sidebar never decides that edge.
 *
 * The heading is screen-reader-only below 1024px (`hideTitleOnMobile`): there the
 * shell's top bar is already showing the page title, and `docs/design` asks that
 * bar to be the calm, single place the user reads where they are. `badge` is
 * NOT hidden — a status a page's whole content depends on has to be readable on
 * a phone, where the heading beside it is not.
 */
export function ManagePage({
  title,
  description,
  badge,
  hideTitle = false,
  primaryAction,
  secondaryLink,
  actions,
  width = "list",
  children,
}: {
  title: string;
  /** One calm line under the title. Optional by design — not every page needs one. */
  description?: string;
  /**
   * A status chip beside the title — for a page whose whole meaning turns on
   * one state (a paused menu item). Never a decoration: if it is not something
   * the reader must know before reading the rest, it does not belong here.
   */
  badge?: ReactNode;
  /**
   * The page's primary action ("Add item"). Declared as DATA, not markup, so
   * the shared header can render it as a labelled button on a desk and collapse
   * it to a `+` on a phone. A `<Button>` passed through `actions` cannot adapt
   * — it stays desktop-sized at 390px and eats a whole row, which is exactly
   * how this app diverged from the seller.
   */
  primaryAction?: PageHeaderAction;
  /**
   * A link that sits beside the primary action — `Edit the page` next to
   * `Publish`. DATA rather than a node so it can ride up to the shell bar with
   * the rest of the header when `hideTitle` is set.
   */
  secondaryLink?: {
    label: string;
    icon: ReactNode;
    to: string;
    testId?: string;
  };
  /**
   * Drop the page heading entirely and let the shell's top bar be the only
   * place the page is named.
   *
   * The same reasoning `hideTitleOnMobile` already applies below 1024px, at
   * every width: the top bar is showing this page's name, so a screen whose
   * header carries nothing else — no badge, no actions — was printing the word
   * twice, once small and once large, one under the other. Only for pages that
   * genuinely have no other header content; anything with a badge or an action
   * still needs the header to hang it on.
   */
  hideTitle?: boolean;
  /** Secondary page-level actions, placed OUTSIDE the list surface. */
  actions?: ReactNode;
  /**
   * `list` → the full width of the content area; `form` → 600px cap.
   *
   * Lists used to cap at 860px and centre, which left a desktop dashboard with
   * a column of content between two margins of empty canvas. The seller portal
   * runs its `main` at `w-full` and lets each screen decide. Forms keep their
   * cap: a 2000px-wide input row is not a dashboard, it is a readability bug.
   */
  width?: "list" | "form";
  children: ReactNode;
}) {
  const titleId = useId();
  /*
   * Resolve the link HERE, where the page's own route is in context.
   *
   * When the header rides up, the `<Link>` is rendered by `ManageShell` — whose
   * nearest route is the layout at `/:locale`, not this page. A relative
   * `editor` therefore resolved to `/en/editor` up there while resolving
   * correctly down here, so the same value meant two different places depending
   * on which side of `hideTitle` the page was on. Publishing an absolute path
   * removes the question.
   */
  const secondaryPath = useResolvedPath(secondaryLink?.to ?? ".");
  const secondary = secondaryLink
    ? { ...secondaryLink, to: secondaryPath.pathname }
    : undefined;
  // Below `lg` the shell's top bar shows this action beside the page title,
  // where the seller portal puts it. `hideActionOnMobile` stops `PageHeader`
  // rendering a second compact copy on a row of its own underneath.
  usePublishPageAction(primaryAction, {
    subtitle: description,
    ownsHeader: hideTitle,
    /* Only when the page has handed its header up: the bar names a page from
       the nav otherwise, and overriding that for a screen that is drawing its
       own title would print the same words twice. */
    title: hideTitle ? title : undefined,
    secondary: hideTitle ? secondary : undefined,
  });

  return (
    <div className={pageFrameClass(width)}>
      {hideTitle ? null : (
        <PageHeader
          title={title}
          subtitle={description}
          badge={badge}
          primaryAction={primaryAction}
          hideActionOnMobile
          actions={
            secondary ? (
              <>
                <Link
                  to={secondary.to}
                  data-testid={secondary.testId}
                  className="inline-flex h-[34px] items-center gap-1.5 rounded-[var(--radius-tile)] border border-[color:var(--border)] px-[13px] text-[11.5px] font-bold text-[color:var(--text-secondary)]"
                >
                  {secondary.icon}
                  {secondary.label}
                </Link>
                {actions}
              </>
            ) : (
              actions
            )
          }
          headingId={titleId}
          hideTitleOnMobile
        />
      )}
      <div className="flex flex-col gap-4" aria-labelledby={titleId}>
        {children}
      </div>
    </div>
  );
}
